import moment from "moment";
import { compact, filter, isNil, last, map, round, split } from "lodash";
import { AICC_CONVERSATION_TYPE } from "../../constants/aicc";
import {
  AiccCanDoStatementResponse,
  AiccConversationAudioResponse,
  AiccConversationContextResponse,
  AiccConversationDetailResponse,
  AiccConversationListResponse,
  AiccConversationMessageResponse,
  AiccConversationResponse,
  AiccFeedbackAnalyticsResponse,
  AiccFeedbackDailyPointResponse,
  AiccFeedbackListResponse,
  AiccFeedbackRecordResponse,
  AiccOnboardingProfileResponse,
  AiccSubUserDetailResponse,
  AiccSubUserListResponse,
  AiccSubUserResponse,
  AiccSystemSettingsResponse,
  AiccUserCanDoStatementsResponse,
  AiccUserVocabularyResponse,
  AiccVocabularyWordResponse,
  CustomerAiccResponse,
} from "../dto/response/aiccCustomer";
import { AiccSystemSettingsRequest } from "../dto/request/aiccCustomer";
import {
  AiccCanDoStatement,
  AiccConversation,
  AiccConversationAudio,
  AiccConversationContext,
  AiccConversationDetail,
  AiccConversationList,
  AiccConversationMessage,
  AiccFeedbackAnalytics,
  AiccFeedbackDailyPoint,
  AiccFeedbackList,
  AiccFeedbackPostAction,
  AiccFeedbackRecord,
  AiccFeedbackType,
  AiccOnboardingProfile,
  AiccSubUser,
  AiccSubUserDetail,
  AiccSubUserList,
  AiccSystemSettings,
  AiccUserCanDoStatements,
  AiccUserVocabulary,
  AiccVocabularyWord,
  CustomerAiccInfo,
} from "../model/aiccCustomer";

export function transformCustomerAicc(response: CustomerAiccResponse): CustomerAiccInfo {
  return {
    isAllAccess: response.is_all_access,
    messageQuotaUsed: response.message_quota_used,
    messageQuotaTotal: response.message_quota_total,
  };
}

export function transformAiccSystemSettings(response: AiccSystemSettingsResponse): AiccSystemSettings {
  return {
    freeUserMessageLimit: response.free_user_message_limit,
    paidUserDailyMessageLimit: response.paid_user_daily_message_limit,
  };
}

export function transformAiccSystemSettingsRequest(settings: AiccSystemSettings): AiccSystemSettingsRequest {
  return {
    free_user_message_limit: settings.freeUserMessageLimit,
    paid_user_daily_message_limit: settings.paidUserDailyMessageLimit,
  };
}

export const DEFAULT_SUB_USER_NAME = "User";

const transformTimestamp = (timestamp?: number) =>
  isNil(timestamp) ? undefined : moment.unix(timestamp).toISOString();

export function transformAiccSubUser(response: AiccSubUserResponse): AiccSubUser {
  return {
    id: response.id,
    name: response.name || DEFAULT_SUB_USER_NAME,
    customerEmail: response.customer_email,
    customerId: response.customer_id,
    studyLanguages: map(response.study_languages, ({ id, name, fluency_level: fluencyLevel }) => ({
      id,
      name,
      fluencyLevel,
    })),
    firstUsedAt: transformTimestamp(response.first_used_at),
    lastActiveAt: transformTimestamp(response.last_active_at),
    isPreview: response.is_preview,
  };
}

export function transformAiccSubUserList(response: AiccSubUserListResponse): AiccSubUserList {
  return {
    items: map(response.items, transformAiccSubUser),
    total: response.total,
  };
}

export function transformAiccSubUserDetail(response: AiccSubUserDetailResponse): AiccSubUserDetail {
  return {
    id: response.id,
    name: response.name || DEFAULT_SUB_USER_NAME,
    customerId: response.customer_id,
    customerEmail: response.customer_email,
    studyLanguages: map(response.study_languages, ({ id, name }) => ({ id, name })),
    isPreview: response.is_preview,
  };
}

export function transformAiccOnboardingProfile(response: AiccOnboardingProfileResponse): AiccOnboardingProfile {
  return {
    preferredName: response.preferred_name,
    preferredGender: response.preferred_gender,
    previousExperience: response.previous_experience,
    learningMotivation: response.learning_motivation,
    preferredTopics: response.preferred_topics,
    confidenceLevel: response.confidence_level,
    other: response.other,
  };
}

export function transformAiccVocabularyWord(response: AiccVocabularyWordResponse): AiccVocabularyWord {
  return {
    word: response.word,
    transliteration: response.transliteration,
    translation: response.translation,
    uses: response.uses ?? 0,
  };
}

export function transformAiccUserVocabulary(response: AiccUserVocabularyResponse): AiccUserVocabulary {
  return {
    known: map(response.known, transformAiccVocabularyWord),
    new: map(response.new, transformAiccVocabularyWord),
    hasTransliteration: response.has_transliteration ?? false,
  };
}

export function transformAiccCanDoStatement(response: AiccCanDoStatementResponse): AiccCanDoStatement {
  return {
    id: response.id,
    statement: response.statement,
    fluencyLevel: response.fluency_level,
    uses: response.uses ?? 0,
  };
}

export function transformAiccUserCanDoStatements(response: AiccUserCanDoStatementsResponse): AiccUserCanDoStatements {
  return {
    items: map(response.statements, transformAiccCanDoStatement),
  };
}

const getLessonReviewTitle = ({ level, lesson }: { level?: number; lesson?: number }) =>
  level && lesson ? `Level ${level} Lesson ${lesson}` : undefined;

const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];

const toNumberWord = (value: number) => NUMBER_WORDS[value] ?? String(value);

const getLessonReviewSummary = ({ context, feedback, is_completed }: AiccConversationDetailResponse) => {
  if (!feedback?.feedback_summary) {
    return;
  }
  const { lesson_script_completed_count: completed, lesson_script_total_count: total } = context ?? {};
  if (isNil(completed) || isNil(total)) {
    return feedback?.feedback_summary;
  }
  if (!is_completed) {
    return "Keep going! Finish this conversation to unlock the next unique practice.";
  }
  if (completed >= total) {
    return `Great work! You completed all ${toNumberWord(
      total
    )} conversations and practiced the full range of phrases included in this Lesson Review.`;
  }
  return `This was ${completed} of ${total} conversations in this Lesson Review. Complete all ${toNumberWord(
    total
  )} to practice the full set of lesson phrases.`;
};

export function transformAiccConversation(response: AiccConversationResponse): AiccConversation {
  const isLessonReview = response.type === AICC_CONVERSATION_TYPE.lessonReview;

  return {
    id: response.id,
    type: response.type,
    title: isLessonReview ? getLessonReviewTitle(response) : response.title,
    status: response.status,
    startedAt: response.started_at,
    completedAt: response.completed_at,
    fluencyLevel: response.fluency_level,
    scriptIndex: response.script_index,
  };
}

export function transformAiccConversationList(response: AiccConversationListResponse): AiccConversationList {
  return {
    items: map(response.items, transformAiccConversation),
    total: response.total,
  };
}

const USER_SENDER = "user";

export function transformAiccConversationMessage(response: AiccConversationMessageResponse): AiccConversationMessage {
  const wordFeedback = response.feedback?.word_feedback;

  return {
    id: response.message_id,
    isUser: response.sender === USER_SENDER,
    content: response.content,
    sentAt: response.sent_at,
    translation: response.translation,
    transliteration: response.transliteration,
    userFeedback: response.user_feedback,
    grammarFeedback: map(
      filter(wordFeedback, ({ grammar_correction: correction }) => !isNil(correction)),
      ({ word, grammar_correction: correction }) => ({
        originalText: correction?.original_text ?? word,
        correctedText: correction?.corrected_text,
        explanation: correction?.explanation,
      })
    ),
    pronunciationFeedback: map(
      filter(wordFeedback, ({ accuracy_score: score }) => !isNil(score)),
      ({ word, accuracy_score: score, pronunciation_audio: audio }) => ({
        word,
        accuracyScore: score as number,
        pronunciationAudio: audio,
      })
    ),
  };
}

export function transformAiccConversationContext(
  response: AiccConversationContextResponse,
  isLessonReview: boolean
): AiccConversationContext {
  return {
    title: isLessonReview ? getLessonReviewTitle(response) : response.title,
    description: response.description,
    contextDescription: response.context_description,
    voiceGender: response.voice_gender,
    conversationFluencyLevel: response.conversation_fluency_level,
    userFluencyLevel: response.user_fluency_level_snapshot,
    isDefaultScenario: response.is_default_scenario,
    canDoStatements: response.can_do_statements ?? [],
    scriptIndex: response.script_index,
    scriptTitle: response.script_title,
  };
}

export function transformAiccConversationDetail(response: AiccConversationDetailResponse): AiccConversationDetail {
  const isLessonReview = response.type === AICC_CONVERSATION_TYPE.lessonReview;

  return {
    id: response.id,
    type: response.type,
    status: response.status,
    startedAt: response.started_at,
    completedAt: response.completed_at,
    context: transformAiccConversationContext(response.context, isLessonReview),
    messages: map(response.messages, transformAiccConversationMessage),
    feedback: {
      summary: isLessonReview ? getLessonReviewSummary(response) : response.feedback?.feedback_summary,
      achievements: response.feedback?.achievements ?? [],
      suggestions: response.feedback?.suggestions ?? [],
    },
  };
}

export function transformAiccConversationAudio(response: AiccConversationAudioResponse): AiccConversationAudio {
  return response?.items ?? {};
}

export const MAX_DOWNVOTE_TURN = 10;

function transformAiccFeedbackDailyPoint(response: AiccFeedbackDailyPointResponse): AiccFeedbackDailyPoint {
  const total = response.thumbs_up_count + response.thumbs_down_count;

  return {
    date: response.date,
    thumbsUp: response.thumbs_up_count,
    thumbsDown: response.thumbs_down_count,
    satisfactionRate: total === 0 ? undefined : round((response.thumbs_up_count / total) * 100, 1),
  };
}

export function transformAiccFeedbackAnalytics(response: AiccFeedbackAnalyticsResponse): AiccFeedbackAnalytics {
  return {
    daily: map(response.daily, transformAiccFeedbackDailyPoint),
    downvoteTurns: map(response.downvote_turns, ({ round: turn, thumbs_down_count: thumbsDown }) => ({
      round: turn,
      label: turn >= MAX_DOWNVOTE_TURN ? `${MAX_DOWNVOTE_TURN}+` : String(turn),
      thumbsDown,
    })),
    abandonment: map(response.abandonment, (point) => ({
      date: point.date,
      continued: point.continued_count,
      retried: point.retried_count,
      rageQuit: point.rage_quit_count,
    })),
  };
}

export function transformAiccFeedbackRecord(response: AiccFeedbackRecordResponse): AiccFeedbackRecord {
  return {
    id: response.id,
    subUserId: response.sub_user_id,
    conversationId: response.conversation_id,
    time: response.created_at,
    language: { id: response.language_id, name: response.language_name },
    type: response.type as AiccFeedbackType,
    downvoteTurn: response.downvote_turn,
    postAction: response.post_downvote_action as AiccFeedbackPostAction | undefined,
    endedAtRound: response.ended_at_round,
    triggerContext: response.trigger_context,
    userFluencyLevelSnapshot: response.user_fluency_level_snapshot,
    customerEmail: response.customer_email,
    userInputDescription: map(response.user_input_description, ({ url, text }) => ({
      id: last(compact(split(url, "/"))) ?? "",
      url,
      text,
    })),
  };
}

export function transformAiccFeedbackList(response: AiccFeedbackListResponse): AiccFeedbackList {
  return {
    items: map(response.items, transformAiccFeedbackRecord),
    total: response.total,
  };
}
